/* eslint-disable prettier/prettier */
import React, { useState, useEffect } from 'react';
import { Grid, makeStyles, TextField, Typography } from '@material-ui/core';

import EditCard from 'components/EditCard';

import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import 'translations/translations';
import {
  ContractInformationRequest,
  LevelOfInvoiceEnumDto,
  SettlementTypeEnumDto,
  AccountingEnumDto,
  CurrencyEnumDto,
} from '.generated/api';
import { useTranslation } from 'react-i18next';
import { organizationActions } from 'actions/organizationActions';
import { notificationActions } from 'actions/notificationActions';

import {
  DateRangePickerComponent,
  utcToLocalWithoutTz,
} from 'components/DateRangePicker';
import { AxiosError } from 'axios';
import LevelOfInvoiceSelect from 'features/common/LevelOfInvoiceSelect';
import SettlementTypeSelect from 'features/common/SettlementTypeSelect';
import AccountingSelect from 'features/common/AccountingSelect';
import CurrencySelect from 'features/common/CurrencySelect';
import ConfirmDialog from 'components/ConfirmDialog';

import { customerNumberRE, guaranteedVolumeRE } from 'helpers/validation';

const useStyles = makeStyles(() => ({
  datePickerItem: {
    width: '25%',
  },
}));

interface ContractCardProps {
  isNextContract?: boolean;
}

const ContractCard: React.FC<ContractCardProps> = ({ isNextContract }) => {
  const classes = useStyles();
  const { t } = useTranslation(['translations']);
  const dispatch = useDispatch();

  const [editMode, setEditMode] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [input, setInput] = useState({
    customerNumber: '',
    startDate: '',
    endDate: '',
    levelOfInvoice: '',
    fullMeasurementPrice: '',
    pieceMeasurementPrice: '',
    settlementType: '',
    accounting: '',
    currency: '',
    guaranteedVolume: '',
  });
  const [isMounted, setIsMounted] = useState(true);

  const { organization, contractInformation } = useSelector(
    (state: ApplicationState) => ({
      organization: state.organization?.organization,
      contractInformation: isNextContract
        ? state.organization?.nextContract
        : state.organization?.currentContract,
    }),
  );

  // track unmount
  useEffect(() => {
    return () => {
      setIsMounted(false);
    };
  }, []);

  // track organization
  useEffect(() => {
    resetInput();
  }, [organization, contractInformation]);

  const resetInput = () => {
    if (contractInformation) {
      setInput({
        customerNumber: contractInformation.customerNumber ?? '',
        startDate: contractInformation.startDate,
        endDate: contractInformation.endDate,
        levelOfInvoice: contractInformation.levelOfInvoice,
        fullMeasurementPrice:
          contractInformation.fullMeasurementPrice.toString(),
        pieceMeasurementPrice:
          contractInformation.pieceMeasurementPrice.toString(),
        settlementType: contractInformation.settlementType.toString(),
        accounting: contractInformation.accounting.toString(),
        currency: contractInformation.currency.toString(),
        guaranteedVolume: contractInformation.guaranteedVolume ?? '',
      });
    } else {
      setInput({
        customerNumber: '',
        startDate: '',
        endDate: '',
        levelOfInvoice: '',
        fullMeasurementPrice: '',
        pieceMeasurementPrice: '',
        settlementType: '',
        accounting: '',
        currency: '',
        guaranteedVolume: '',
      });
    }
  };

  const handleCustomerNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = e.target;
    if (
      value.length > input.customerNumber.length && // always allow character deletion
      !customerNumberRE.test(value)
    ) {
      return;
    }
    setInput((input) => ({ ...input, customerNumber: value }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInput((input) => ({ ...input, [name]: value }));
  };

  const onStartDateChange = (dateValue?: Date) => {
    if (dateValue) {
      setInput((input) => ({
        ...input,
        startDate: dateValue.toISOString(),
      }));
    }
  };

  const onEndDateChange = (dateValue?: Date) => {
    if (dateValue) {
      setInput((input) => ({
        ...input,
        endDate: dateValue.toISOString(),
      }));
    }
  };

  const handleDateRangeChange = (
    from: Date | null,
    to: Date | null,
    valid: boolean,
  ) => {
    onStartDateChange(from && valid ? from : undefined);
    onEndDateChange(to && valid ? to : undefined);
  };

  const handleGuaranteedVolumeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = e.target;
    if (!guaranteedVolumeRE.test(value)) {
      return;
    }
    setInput((input) => ({ ...input, guaranteedVolume: value }));
  };

  const getFormattedDate = (
    dateString: string | undefined,
  ): string | undefined => {
    const format = navigator.language;
    if (dateString) {
      const dateObject = utcToLocalWithoutTz(new Date(dateString));
      return dateObject.toLocaleDateString(format);
    } else {
      return undefined;
    }
  };

  const saveHandler = (error?: AxiosError) => {
    if (isMounted) {
      setIsLoading(false);
      setEditMode(!!error);
    }
    if (error) {
      dispatch(notificationActions.enqueueErrorResponse(error));
    }
  };

  const handleSave = () => {
    if (organization && input?.startDate && input?.endDate) {
      setIsLoading(true);
      const inputToSave: ContractInformationRequest = {
        customerNumber:
          input.levelOfInvoice === LevelOfInvoiceEnumDto.DEPARTMENT ||
          input.customerNumber.length === 0
            ? null
            : input.customerNumber,
        startDate: input.startDate,
        endDate: input.endDate,
        levelOfInvoice: input.levelOfInvoice as LevelOfInvoiceEnumDto,
        fullMeasurementPrice: parseFloat(input.fullMeasurementPrice),
        pieceMeasurementPrice: parseFloat(input.pieceMeasurementPrice),
        settlementType: input.settlementType as SettlementTypeEnumDto,
        accounting: input.accounting as AccountingEnumDto,
        currency: input.currency as CurrencyEnumDto,
        guaranteedVolume: input.guaranteedVolume,
      };
      dispatch(
        isNextContract
          ? organizationActions.updateNextContract(
              organization.organizationId,
              inputToSave,
              saveHandler,
            )
          : organizationActions.updateCurrentContract(
              organization.organizationId,
              inputToSave,
              saveHandler,
            ),
      );
    }
  };

  const handleEdit = () => {
    if (isNextContract) {
      handleConfirmDialogYes();
    } else {
      setConfirmDialogOpen(true);
    }
  };

  const handleConfirmDialogYes = () => {
    resetInput();
    setConfirmDialogOpen(false);
    setEditMode(true);
  };

  const handleConfirmDialogNo = () => {
    setConfirmDialogOpen(false);
  };

  const handleCancel = () => {
    setEditMode(false);
  };

  const getLevelOfInvoice = (levelOfInvoice: LevelOfInvoiceEnumDto) => {
    switch (levelOfInvoice) {
      case LevelOfInvoiceEnumDto.ORGANIZATION:
        return t('labels.organization');
      case LevelOfInvoiceEnumDto.DEPARTMENT:
        return t('labels.department');
      default:
        return '';
    }
  };

  const getSettlementType = (getSettlementType: SettlementTypeEnumDto) => {
    return (
      t(
        `organization_page.contract.settlement_type.${getSettlementType.toLowerCase()}`,
      ) || ''
    );
  };

  const getAccounting = (getAccounting: AccountingEnumDto) => {
    return (
      t(
        `organization_page.contract.accounting_type.${getAccounting.toLowerCase()}`,
      ) || ''
    );
  };

  const getCurrency = (getCurrency: CurrencyEnumDto) => {
    return (
      t(`organization_page.contract.currency.${getCurrency.toLowerCase()}`) ||
      ''
    );
  };

  return (
    <EditCard
      editMode={editMode}
      isLoading={isLoading}
      isValid={
        input.startDate.length > 0 &&
        input.endDate.length > 0 &&
        input.fullMeasurementPrice.length > 0 &&
        input.pieceMeasurementPrice.length > 0
      }
      title={
        isNextContract
          ? t('organization_page.contract.next_contract_title')
          : t('organization_page.contract.current_contract_title')
      }
      onEdit={handleEdit}
      onCancel={handleCancel}
      onSave={handleSave}
      requireGlobalAdmin
    >
      {editMode ? (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <LevelOfInvoiceSelect
              onChange={handleChange}
              value={input?.levelOfInvoice ?? ''}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              type="text"
              variant="outlined"
              fullWidth
              label={t('organization_page.contract.labels.customer_no')}
              name="customerNumber"
              value={
                input?.levelOfInvoice === LevelOfInvoiceEnumDto.DEPARTMENT
                  ? ''
                  : input?.customerNumber ?? ''
              }
              onChange={handleCustomerNumberChange}
              disabled={
                input?.levelOfInvoice === LevelOfInvoiceEnumDto.DEPARTMENT
              }
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              variant="outlined"
              fullWidth
              type="number"
              label={t(
                'organization_page.contract.labels.price_full_measurement',
              )}
              InputProps={{ inputProps: { min: 0 } }}
              name="fullMeasurementPrice"
              onChange={handleChange}
              value={input?.fullMeasurementPrice ?? ''}
            ></TextField>
          </Grid>

          <Grid item xs={6}>
            <TextField
              variant="outlined"
              fullWidth
              type="number"
              label={t(
                'organization_page.contract.labels.price_piece_measurement',
              )}
              InputProps={{ inputProps: { min: 0 } }}
              name="pieceMeasurementPrice"
              onChange={handleChange}
              value={input?.pieceMeasurementPrice ?? ''}
            ></TextField>
          </Grid>

          <Grid item xs={6}>
            <SettlementTypeSelect
              onChange={handleChange}
              value={input?.settlementType ?? ''}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              type="text"
              variant="outlined"
              fullWidth
              label={t('organization_page.contract.labels.guaranteed_volume')}
              name="guaranteedVolume"
              onChange={handleGuaranteedVolumeChange}
              value={input?.guaranteedVolume ?? ''}
            />
          </Grid>

          <Grid item xs={6}>
            <AccountingSelect
              onChange={handleChange}
              value={input?.accounting ?? ''}
            />
          </Grid>

          <Grid item xs={6}>
            <CurrencySelect
              onChange={handleChange}
              value={input?.currency ?? ''}
            />
          </Grid>

          <DateRangePickerComponent
            utc
            onChange={handleDateRangeChange}
            itemClass={classes.datePickerItem}
            defaultStartDate={
              input.startDate.length > 0 ? new Date(input.startDate) : null
            }
            defaultEndDate={
              input.endDate.length > 0 ? new Date(input.endDate) : null
            }
          />
        </Grid>
      ) : (
        <>
          <Typography variant="body1">
            <b>
              {t('labels.level_of_invoice')}
              :&nbsp;
            </b>
            {contractInformation &&
              getLevelOfInvoice(contractInformation.levelOfInvoice)}
          </Typography>
          <Typography variant="body1">
            <b>
              {t('organization_page.contract.labels.customer_no')}
              :&nbsp;
            </b>
            {contractInformation?.customerNumber ?? ''}
          </Typography>
          <Typography variant="body1">
            <b>
              {t('organization_page.contract.labels.full_measurement')}
              :&nbsp;
            </b>
            {contractInformation?.fullMeasurementPrice ?? ''}
          </Typography>
          <Typography variant="body1">
            <b>
              {t('organization_page.contract.labels.piece_measurement')}
              :&nbsp;
            </b>
            {contractInformation?.pieceMeasurementPrice ?? ''}
          </Typography>
          <Typography variant="body1">
            <b>
              {t('organization_page.contract.labels.settlement_type')}
              :&nbsp;
            </b>
            {contractInformation &&
              getSettlementType(contractInformation.settlementType)}
          </Typography>
          <Typography variant="body1">
            <b>
              {t('organization_page.contract.labels.guaranteed_volume')}
              :&nbsp;
            </b>
            {contractInformation?.guaranteedVolume ?? ''}
          </Typography>
          <Typography variant="body1">
            <b>
              {t('organization_page.contract.labels.accounting')}
              :&nbsp;
            </b>
            {contractInformation &&
              getAccounting(contractInformation.accounting)}
          </Typography>
          <Typography variant="body1">
            <b>
              {t('organization_page.contract.labels.currency')}
              :&nbsp;
            </b>
            {contractInformation && getCurrency(contractInformation.currency)}
          </Typography>
          <Typography variant="body1">
            <b>
              {t('organization_page.contract.labels.contract_date')}
              :&nbsp;
            </b>
            {getFormattedDate(contractInformation?.startDate)}
            &nbsp;-&nbsp;
            {getFormattedDate(contractInformation?.endDate)}
          </Typography>
          {/* <Typography variant="body1">
            <b>
              {t('organization_page.contract.labels.retention_duration')}:
              &nbsp;
            </b>
            {contractInformation?.retentionDuration}
          </Typography> */}
        </>
      )}
      <ConfirmDialog
        isOpen={confirmDialogOpen}
        title={t('organization_page.contract.edit_dialog.title')}
        text={t('organization_page.contract.edit_dialog.text')}
        confirmButtonText={t('common.ok')}
        onConfirm={handleConfirmDialogYes}
        onCancel={handleConfirmDialogNo}
      />
    </EditCard>
  );
};

export default ContractCard;
